import { createSectionTitle, createSummaryTitle, createTable, parseHtmlContent } from '../utils';

export const renderHPI = (data, title) => {
  const content = [createSectionTitle(title)];

  // Handle noDetails case
  if (data && data.noDetails) {
    content.push({
      text: 'No details',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
    return content;
  }

  // Helper function to check if HPI data is meaningful
  const hasValidHPIData = (data) => {
    if (!data || typeof data !== 'object') return false;

    // Check main HPI fields
    const hpiFields = [
      data.location?.label || data.location?.value,
      data.duration?.label || data.duration?.value,
      data.timing?.label || data.timing?.value,
      data.severity?.label || data.severity?.value,
      data.quality?.label || data.quality?.value,
      data.context?.label || data.context?.value,
      data.contextNote,
      data.relievingFactors?.label || data.relievingFactors?.value,
      data.relievingFactorsNote,
      data.signsAndSymptoms
    ].some((value) => value && value.trim() !== '');

    // Check modifying factors
    const hasModifyingFactors =
      data.modifyingFactors &&
      !data.modifyingFactors.noModifyingFactors &&
      (data.modifyingFactors.takenMedication ||
        data.modifyingFactors.appliedHeat ||
        data.modifyingFactors.appliedColdCompress ||
        data.modifyingFactors.hadSurgery ||
        data.modifyingFactors.triedPhysicalTherapy ||
        data.modifyingFactors.beenRespirator ||
        data.modifyingFactors.triedRest ||
        (data.modifyingFactors.otherModifyingFactors &&
          data.modifyingFactors.otherModifyingFactors.trim() !== ''));

    const hasNarrative = data.narrative && data.narrative.trim() !== '';
    const hasNoModifyingFactorsFlag =
      data.modifyingFactors && data.modifyingFactors.noModifyingFactors;

    return hpiFields || hasModifyingFactors || hasNarrative || hasNoModifyingFactorsFlag;
  };

  // Check if there's any meaningful data to display
  if (!hasValidHPIData(data)) {
    content.push({
      text: 'No data',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
    return content;
  }

  const hpiFields = [
    { label: 'Location', value: data.location?.label || data.location?.value || '' },
    { label: 'Duration', value: data.duration?.label || data.duration?.value || '' },
    { label: 'Timing', value: data.timing?.label || data.timing?.value || '' },
    { label: 'Severity', value: data.severity?.label || data.severity?.value || '' },
    { label: 'Quality', value: data.quality?.label || data.quality?.value || '' },
    { label: 'Context', value: data.context?.label || data.context?.value || '' },
    { label: 'Context Note', value: data.contextNote || '' },
    {
      label: 'Relieving Factors',
      value: data.relievingFactors?.label || data.relievingFactors?.value || ''
    },
    { label: 'Relieving Factors Note', value: data.relievingFactorsNote || '' },
    { label: 'Signs and Symptoms', value: data.signsAndSymptoms || '' }
  ];

  const rows = hpiFields
    .filter((field) => field.value && field.value.trim() !== '')
    .map((field) => [
      { text: field.label, style: 'tableHeader' },
      { text: field.value, style: 'tableCell' }
    ]);

  if (rows.length > 0) {
    content.push(createTable(['Field', 'Value'], rows, ['30%', '70%']));
  }

  if (data.modifyingFactors && !data.modifyingFactors.noModifyingFactors) {
    const modifyingFactorsRows = [];

    if (data.modifyingFactors.takenMedication) {
      modifyingFactorsRows.push([
        { text: 'Medication Taken', style: 'tableHeader' },
        { text: data.modifyingFactors.medication || '', style: 'tableCell' },
        { text: data.modifyingFactors.helpedMedication || '', style: 'tableCell' }
      ]);
    }

    if (data.modifyingFactors.appliedHeat) {
      modifyingFactorsRows.push([
        { text: 'Applied Heat', style: 'tableHeader' },
        { text: 'Yes', style: 'tableCell' },
        { text: data.modifyingFactors.helpedHeat || '', style: 'tableCell' }
      ]);
    }

    if (data.modifyingFactors.appliedColdCompress) {
      modifyingFactorsRows.push([
        { text: 'Applied Cold Compress', style: 'tableHeader' },
        { text: 'Yes', style: 'tableCell' },
        { text: data.modifyingFactors.helpedColdCompress || '', style: 'tableCell' }
      ]);
    }

    if (data.modifyingFactors.hadSurgery) {
      modifyingFactorsRows.push([
        { text: 'Had Surgery', style: 'tableHeader' },
        { text: 'Yes', style: 'tableCell' },
        { text: data.modifyingFactors.helpedSurgery || '', style: 'tableCell' }
      ]);
    }

    if (data.modifyingFactors.triedPhysicalTherapy) {
      modifyingFactorsRows.push([
        { text: 'Tried Physical Therapy', style: 'tableHeader' },
        { text: 'Yes', style: 'tableCell' },
        { text: data.modifyingFactors.helpedPhysicalTherapy || '', style: 'tableCell' }
      ]);
    }

    if (data.modifyingFactors.beenRespirator) {
      modifyingFactorsRows.push([
        { text: 'Been on Respirator', style: 'tableHeader' },
        { text: 'Yes', style: 'tableCell' },
        { text: data.modifyingFactors.helpedRespirator || '', style: 'tableCell' }
      ]);
    }

    if (data.modifyingFactors.triedRest) {
      modifyingFactorsRows.push([
        { text: 'Tried Rest', style: 'tableHeader' },
        { text: 'Yes', style: 'tableCell' },
        { text: data.modifyingFactors.helpedRest || '', style: 'tableCell' }
      ]);
    }

    if (
      data.modifyingFactors.otherModifyingFactors &&
      data.modifyingFactors.otherModifyingFactors.trim() !== ''
    ) {
      modifyingFactorsRows.push([
        { text: 'Other Modifying Factors', style: 'tableHeader' },
        { text: data.modifyingFactors.otherModifyingFactors, style: 'tableCell' },
        { text: data.modifyingFactors.helpedOtherModifyingFactors || '', style: 'tableCell' }
      ]);
    }

    if (modifyingFactorsRows.length > 0) {
      content.push({
        text: 'MODIFYING FACTORS',
        style: 'summaryHeader',
        margin: [0, 10, 0, 3]
      });
      content.push(
        createTable(['Factor', 'Details', 'Helped'], modifyingFactorsRows, ['40%', '30%', '30%'])
      );
    }
  } else if (data.modifyingFactors && data.modifyingFactors.noModifyingFactors) {
    content.push({
      text: 'MODIFYING FACTORS',
      style: 'summaryHeader',
      margin: [0, 10, 0, 3]
    });
    content.push({
      text: 'No modifying factors reported',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
  }

  // Show narrative if it exists
  if (data.narrative && data.narrative.trim() !== '') {
    content.push(createSummaryTitle());
    content.push({
      text: parseHtmlContent(data.narrative),
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
  }

  // If no content was added beyond title, show "No data"
  if (content.length === 1) {
    content.push({
      text: 'No data',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
  }

  return content;
};
